import { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';

import {
  FilterCheckboxItem,
  FilterItemNoResults,
  FilterSearch,
  FiltersList,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersDrawer/FiltersDrawer';
import IconAssociate from '@/icons/Associate.svg';

import { useFilters } from '../FiltersState';
import { useGroupFilterState } from '../shared';

const MINIMUM_GROUPS_FOR_SEARCH = 5;

export const FiltersGroup = () => {
  const { t } = useLingui();

  const {
    groups: { groups, setGroups },
  } = useFilters();

  const { search, setSearch, fetchNextPage, isLoading, entityGroups, count } =
    useGroupFilterState();

  const selectedGroupsIds = groups.map(({ id }) => id);

  const options: ReactNode[] = entityGroups.map((entityGroup) => (
    <FilterCheckboxItem key={entityGroup.id} id={entityGroup.id} label={entityGroup.name} />
  ));

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-mobile" />);
  }

  const toggleGroup = (groupId: string) => {
    const group = entityGroups.find(({ id }) => id === groupId);

    if (group) {
      if (selectedGroupsIds.includes(group.id)) {
        setGroups(groups.filter(({ id }) => id !== group.id));
      } else {
        setGroups([...groups, group]);
      }
    }
  };

  const optionsWithSearch = (
    <FilterSearch
      ariaLabel={t({
        id: 'settings.organization.entities.entities.filters.group.label',
        message: 'Group',
      })}
      inputProps={{
        ariaLabel: t({
          id: 'settings.organization.entities.entities.filters.group.search.label',
          message: 'Search groups',
        }),
        placeholder: t({
          id: 'settings.organization.entities.entities.filters.group.search.placeholder',
          message: 'Search groups...',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={entityGroups.length === 0 && isLoading}
      isLoadingMore={isLoading}
      values={selectedGroupsIds}
      onChange={toggleGroup}
    >
      {options}
    </FilterSearch>
  );

  const optionsWithoutSearch = (
    <FiltersList
      ariaLabel={t({
        id: 'settings.organization.entities.entities.filters.group.label',
        message: 'Group',
      })}
      values={selectedGroupsIds}
      onChange={toggleGroup}
    >
      {options}
    </FiltersList>
  );

  return (
    <FiltersSubMenu
      id="group"
      icon={<IconAssociate />}
      label={t({
        id: 'settings.organization.entities.entities.filters.group.label',
        message: 'Group',
      })}
      count={groups.length}
    >
      {count < MINIMUM_GROUPS_FOR_SEARCH ? optionsWithoutSearch : optionsWithSearch}
    </FiltersSubMenu>
  );
};
